import { useCallback, useMemo } from "react";
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DropDownMultiple from "../../staff/DropDownMultiple";
import type { IAreaOfPractice } from "../../../interfaces/models/IAreaOfPractice";
import { usePersonForm } from "./personForm/PersonFormContext";
import { aopRankNamesPatchFromOrder } from "../../../utils/aopRankNames";
import CheckBoxGroup from "../../staff/CheckBoxGroup";
import DragPanIcon from "../../Atomos/Icons/Drag_pan";
import CustomScrollContainer from "../../Atomos/CustomScrollContainer";

function syncExpertiseToMyAOP(
  prevOrder: string[] | undefined,
  aopExpertiseIds: string[]
): string[] {
  const mySet = new Set(aopExpertiseIds);
  const next: string[] = [];
  const used = new Set<string>();
  for (const id of prevOrder ?? []) {
    if (mySet.has(id) && !used.has(id)) {
      next.push(id);
      used.add(id);
    }
  }
  for (const id of aopExpertiseIds) {
    if (!used.has(id)) {
      next.push(id);
      used.add(id);
    }
  }
  return next;
}

interface AOPProps {
  firmAreasOfPractice: IAreaOfPractice[];
}

interface SortableExpertiseCardProps {
  id: string;
  label: string;
}

function SortableExpertiseCard({ id, label }: SortableExpertiseCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.65 : 1,
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex w-full min-w-0 items-center gap-3 rounded-md bg-gray-25 px-3 py-2 shadow-sm"
    >
      <button
        type="button"
        className="touch-none shrink-0 cursor-grab p-0.5 text-gray-font hover:text-gray-800 active:cursor-grabbing"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <DragPanIcon size={18} />
      </button>
      <span className="min-w-0 text-sm font-bold leading-snug text-gray-font">
        {label}
      </span>
    </div>
  );
}

const AOP = ({ firmAreasOfPractice }: AOPProps) => {
  const {
    form,
    setFormPatch,
    touchFields,
    getFieldError,
    shouldShowError,
  } = usePersonForm();

  const aopRequired = Boolean(form.isAttorney);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor)
  );

  const aopById = useMemo(() => {
    const m = new Map<string, IAreaOfPractice>();
    for (const a of firmAreasOfPractice) {
      m.set(a.id, a);
    }
    return m;
  }, [firmAreasOfPractice]);

  const options = useMemo(
    () =>
      firmAreasOfPractice.map((a) => ({
        id: a.id,
        name: a.displayName,
      })),
    [firmAreasOfPractice]
  );

  const areasIds = useMemo(
    () => form.areasOfPractice ?? [],
    [form.areasOfPractice]
  );

  const myAOPIds = useMemo(() => form.aopExpertiseIds ?? [], [form.aopExpertiseIds]);

  const dropdownValue = useMemo(() => {
    const ids = new Set(areasIds);
    return firmAreasOfPractice
      .filter((a) => ids.has(a.id))
      .map((a) => ({ id: a.id, name: a.displayName }));
  }, [areasIds, firmAreasOfPractice]);

  const poolAops = useMemo(() => {
    const ids = new Set(areasIds);
    return firmAreasOfPractice
      .filter((a) => ids.has(a.id))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [areasIds, firmAreasOfPractice]);

  const expertiseIds = useMemo(
    () => form.aopExpertiseIds ?? [],
    [form.aopExpertiseIds]
  );

  const handleAreasOfPracticeChange = useCallback(
    (nextIds: string[]) => {
      const prunedMy = myAOPIds.filter((id) => nextIds.includes(id));
      const syncedOrder = syncExpertiseToMyAOP(form.aopExpertiseIds, prunedMy);
      setFormPatch({
        areasOfPractice: nextIds,
        aopExpertiseIds: syncedOrder,
        ...aopRankNamesPatchFromOrder(syncedOrder, aopById),
      });
      touchFields(["areasOfPractice", "aopExpertiseIds"]);
    },
    [
      form.aopExpertiseIds,
      myAOPIds,
      aopById,
      setFormPatch,
      touchFields,
    ]
  );

  const handleMyAOPChange = useCallback(
    (next: string[]) => {
      const capped = next.slice(0, 4);
      const syncedOrder = syncExpertiseToMyAOP(form.aopExpertiseIds, capped);
      setFormPatch({
        aopExpertiseIds: syncedOrder,
        ...aopRankNamesPatchFromOrder(syncedOrder, aopById),
      });
      touchFields(["aopExpertiseIds"]);
    },
    [form.aopExpertiseIds, aopById, setFormPatch, touchFields]
  );

  const handleExpertiseDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const items = [...(form.aopExpertiseIds ?? [])];
      const oldIndex = items.indexOf(String(active.id));
      const newIndex = items.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) {
        return;
      }
      const nextOrder = arrayMove(items, oldIndex, newIndex);
      setFormPatch({
        aopExpertiseIds: nextOrder,
        ...aopRankNamesPatchFromOrder(nextOrder, aopById),
      });
      touchFields(["aopExpertiseIds"]);
    },
    [form.aopExpertiseIds, aopById, setFormPatch, touchFields]
  );

  return (
    <div className="grid w-full grid-cols-12 gap-5 lg:divide-x divide-gray-200">
      <div className="flex min-w-0 flex-col gap-3 col-span-12 lg:col-span-5 2xl:col-span-6">
        <h4 className="text-lg lg:text-[20px] font-bold text-gray-font">
          Select Areas of Practice 
          <span className="text-gray-500 font-normal text-base lg:text-lg"> {` (${firmAreasOfPractice.length} AOPs)`} </span>
          {aopRequired && <span className="text-primary">*</span>}
        </h4>
        <DropDownMultiple
          title="Search Firm's and Office's Areas of Practice"
          options={options}
          value={dropdownValue}
          multiple
          searchable
          onChange={(value) => {
            handleAreasOfPracticeChange(
              value.map((v) =>
                typeof v === "object" && v && "id" in v
                  ? String(v.id)
                  : String(v)
              )
            );
          }}
          error={
            shouldShowError("areasOfPractice") &&
            Boolean(getFieldError("areasOfPractice"))
          }
          errorMessage={getFieldError("areasOfPractice")}
        />
        <div
          className="w-full min-w-0"
          aria-label="Areas of practice list, scroll for more options"
        >
          <CustomScrollContainer
            direction="vertical"
            showScrollbar="auto"
            className="h-[200px] lg:h-[350px] 2xl:h-[400px] min-h-0 p-2"
            containerClassName="rounded-md border border-gray-200"
          >
            <CheckBoxGroup<IAreaOfPractice>
              contentClassName="grid grid-cols-1 gap-2 sm:grid-cols-2"
              options={firmAreasOfPractice}
              value={areasIds}
              onChange={handleAreasOfPracticeChange}
              getOptionValue={(a) => a.id ?? ""}
              getOptionLabel={(a) => a.displayName}
            />
          </CustomScrollContainer>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 col-span-12 lg:col-span-7 2xl:col-span-6 lg:pl-4">
        <div className="flex min-h-0 min-w-0 flex-col gap-3">
          <h4 className="text-lg lg:text-[20px] font-bold text-gray-font">
            My Areas of Practice {aopRequired && <span className="text-primary">*</span>}
          </h4>
          <p className="text-xs text-gray-500">
            Select up to 4 areas for your profile emphasis.
          </p>
          <CustomScrollContainer
            direction="vertical"
            showScrollbar="auto"
            className="h-auto lg:h-[285px] min-h-0" 
          >
            <CheckBoxGroup<IAreaOfPractice>
              contentClassName="flex flex-col gap-2"
              options={poolAops}
              value={myAOPIds}
              onChange={handleMyAOPChange}
              getOptionValue={(a) => a.id ?? ""}
              getOptionLabel={(a) => a.displayName}
              getOptionDisabled={(a) => {
                const id = a.id ?? "";
                return (
                  myAOPIds.length >= 4 && !myAOPIds.includes(id)
                );
              }}
              error={
                shouldShowError("aopExpertiseIds") && Boolean(getFieldError("aopExpertiseIds"))
              }
              errorMessage={getFieldError("aopExpertiseIds")}
            />
          </CustomScrollContainer>
        </div>
        <div className="flex flex-col gap-3">
            <h4 className="text-lg lg:text-[20px] font-bold text-gray-font">
              My Expertise {aopRequired && <span className="text-primary">*</span>}
            </h4>
            <p className="text-xs text-gray-500">
              Choose up to 4 areas in “My Areas of Practice” to set
              expertise order.
            </p>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleExpertiseDragEnd}
          >
              {!!expertiseIds.length && (
            <div>
              <div className="mb-3 grid grid-cols-7 gap-x-4">
                <div className="col-span-1 text-sm font-semibold text-gray-font">
                  Order
                </div>
                <div className="text-sm font-semibold text-gray-font">
                  Expertise
                </div>
              </div>
                <SortableContext
                  items={expertiseIds}
                  strategy={verticalListSortingStrategy}
                >
                  <ul className="m-0 flex list-none flex-col gap-3 p-0">
                    {expertiseIds.map((id, index) => (
                      <li
                        key={id}
                        className="grid grid-cols-7 items-center justify-center gap-x-4"
                      >
                        <span className="col-span-1 font-medium text-gray-font text-center">
                          {index + 1}.
                        </span>
                        <div className="col-span-6">
                          <SortableExpertiseCard
                            id={id}
                            label={aopById.get(id)?.displayName ?? id}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                </SortableContext>
            </div>
              )}
          </DndContext>

          {expertiseIds.length ? <p className="text-xs text-gray-500">
            * To change order, click on the drag icon and drag the element to a
            new spot within the list.
          </p> : null}
        </div>

      </div>
    </div>
  );
};

export default AOP;
